import { motion } from 'framer-motion';
import { useOutletContext } from 'react-router-dom';
import { Link } from 'react-router-dom';
import {
  Brain, Users, TrendingUp, MessageSquare, Zap, Activity,
  ArrowRight, ExternalLink, ChevronRight, Sparkles,
} from 'lucide-react';

interface OutletCtx { isUnlocked: boolean; isAdmin: boolean; }

const WALKTHROUGH_URL = 'https://sessionintelligence.app/walkthrough';

const METRICS = [
  { label: 'Session Reports Analysed', value: '12', sub: 'Demo preview', icon: Brain, color: 'text-success-50', bg: 'bg-success-10' },
  { label: 'Clients Tracked', value: '5', sub: 'Demo preview', icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
  { label: 'AI-Detected Pattern Signals', value: '18', sub: 'Across sample sessions', icon: TrendingUp, color: 'text-orange-50', bg: 'bg-orange-10' },
  { label: 'AI-Generated Coach Questions', value: '42', sub: 'For next-session prep', icon: MessageSquare, color: 'text-teal-50', bg: 'bg-teal-10' },
  { label: 'AI-Suggested Breakthrough Zones', value: '7', sub: 'Demo preview', icon: Zap, color: 'text-success-50', bg: 'bg-success-10' },
];

const DEMO_CLIENTS = [
  {
    name: 'Alex',
    lastSession: '18 Jun',
    theme: 'Visibility and confidence',
    breakthroughZone: 'Fear of judgement',
    nextFocus: 'Identity around being seen',
    progressSignal: 'More action language emerging',
    initials: 'AL',
    color: 'bg-blue-500',
  },
  {
    name: 'Sarah',
    lastSession: '17 Jun',
    theme: 'Boundaries and resentment',
    breakthroughZone: 'Saying no without guilt',
    nextFocus: 'Values conflict: peace vs approval',
    progressSignal: 'Clearer emotional ownership',
    initials: 'SA',
    color: 'bg-success-40',
  },
  {
    name: 'David',
    lastSession: '15 Jun',
    theme: 'Follow-through and avoidance',
    breakthroughZone: 'Internal conflict around success',
    nextFocus: 'Ambition vs safety',
    progressSignal: 'Reduced "I can\'t" language',
    initials: 'DA',
    color: 'bg-orange-40',
  },
];

const PATTERN_CATEGORIES = [
  { label: 'Meta Model Patterns', count: 11, max: 11 },
  { label: 'Emotional Signals', count: 9, max: 11 },
  { label: 'Limiting Beliefs', count: 6, max: 11 },
  { label: 'Values Clues', count: 5, max: 11 },
  { label: 'Avoidance Patterns', count: 4, max: 11 },
  { label: 'Identity Statements', count: 4, max: 11 },
  { label: 'Parts Conflicts', count: 3, max: 11 },
];

const ACTIVITY_FEED = [
  "Alex's latest report identified a possible visibility breakthrough zone.",
  "Sarah's session showed repeated language around guilt and responsibility.",
  "David's language shifted from \"I can't\" to \"I'm starting to notice.\"",
  '12 new coach questions generated from recent sessions.',
  '3 follow-up items ready for review.',
  '2 possible values conflicts detected.',
];

const PROGRESS_SIGNALS = [
  { session: 'Session 1', label: 'Fixed language', quote: '"I can\'t," "I don\'t know," "I always stop."', color: 'border-l-red-300 bg-red-50' },
  { session: 'Session 2', label: 'Emerging possibility', quote: '"Part of me wants to," "I\'m starting to see," "Maybe I could."', color: 'border-l-orange-300 bg-orange-50' },
  { session: 'Session 3', label: 'Ownership language', quote: '"I chose," "I followed through," "I noticed the pattern."', color: 'border-l-success-40 bg-success-5' },
];

function DemoLabel({ text = 'Demo Preview' }: { text?: string }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-orange-10 text-orange-70 text-xs font-semibold">
      {text}
    </span>
  );
}

export function SIDashboard() {
  const { isUnlocked } = useOutletContext<OutletCtx>();

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-80">Welcome to Your Session Intelligence<sup className="text-sm font-normal">™</sup> Dashboard</h1>
            <p className="text-sm text-gray-50 mt-1.5 max-w-2xl leading-relaxed">
              This is your <strong className="text-gray-70">AI-assisted coaching intelligence centre</strong> — designed to help you analyse sessions, identify client patterns, prepare better questions, and track progress across your coaching work.
            </p>
            {!isUnlocked && (
              <p className="text-sm text-gray-50 mt-1">
                Your first session upload will be activated during the live walkthrough.
              </p>
            )}
          </div>
          {!isUnlocked && (
            <a
              href={WALKTHROUGH_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-success-40 hover:bg-success-50 text-white rounded-2xl text-sm font-semibold transition-colors flex-shrink-0 shadow-soft"
            >
              Join Live Walkthrough
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>
      </motion.div>

      {/* Metrics row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {METRICS.map((m, i) => (
          <motion.div
            key={m.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06 }}
            className="bg-white rounded-2xl border border-gray-20 p-4"
          >
            <div className={`w-8 h-8 ${m.bg} rounded-xl flex items-center justify-center mb-3`}>
              <m.icon className={`w-4 h-4 ${m.color}`} />
            </div>
            <p className="text-2xl font-bold text-gray-80">{m.value}</p>
            <p className="text-xs text-gray-60 font-medium mt-0.5 leading-tight">{m.label}</p>
            <DemoLabel text={m.sub} />
          </motion.div>
        ))}
      </div>

      {/* Coach Intelligence AI card */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.12 }}
        className="bg-gradient-to-br from-success-40 to-teal-50 rounded-2xl p-6 text-white"
      >
        <div className="flex flex-col sm:flex-row sm:items-start gap-5">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-white" />
              <h2 className="text-base font-bold text-white">Coach Intelligence AI</h2>
            </div>
            <p className="text-sm text-white/80 leading-relaxed mb-4">
              Ask questions about your session reports, client patterns, breakthrough zones, next-session focus, and follow-up ideas.
              Coach Intelligence AI helps you think through what may be happening in your client sessions and how to prepare more effectively.
            </p>
            <div className="flex flex-wrap gap-2 mb-5">
              {[
                '"What patterns are showing up across these sessions?"',
                '"What should I explore next with Alex?"',
                '"Give me 5 questions for Sarah\'s next session."',
                '"What language suggests a possible limiting belief?"',
              ].map((prompt, i) => (
                <span key={i} className="inline-flex items-center px-2.5 py-1 bg-white/15 rounded-xl text-xs text-white/90 italic">
                  {prompt}
                </span>
              ))}
            </div>
            <Link
              to="/si/app/coach-ai"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-success-50 hover:bg-white/90 rounded-xl text-sm font-bold transition-colors"
            >
              Open Coach Intelligence AI
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="hidden sm:flex w-20 h-20 rounded-2xl bg-white/15 items-center justify-center flex-shrink-0">
            <Brain className="w-10 h-10 text-white" />
          </div>
        </div>
        {!isUnlocked && (
          <p className="text-xs text-white/60 mt-4 border-t border-white/20 pt-3">
            Your Coach Intelligence AI will activate when your first Session Intelligence™ report is unlocked during the live walkthrough.{' '}
            <a href={WALKTHROUGH_URL} target="_blank" rel="noopener noreferrer" className="underline text-white/80 hover:text-white">
              Join the Live Walkthrough
            </a>
          </p>
        )}
      </motion.div>

      {/* Two-column grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Client Insight Snapshot — 2 cols */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="lg:col-span-2 bg-white rounded-2xl border border-gray-20 overflow-hidden"
        >
          <div className="px-5 py-4 border-b border-gray-10 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-gray-80">Client Insight Snapshot</h2>
              <p className="text-xs text-gray-40 mt-0.5">Sample client data — your clients will appear here after unlock</p>
            </div>
            <DemoLabel />
          </div>
          <div className="divide-y divide-gray-10">
            {DEMO_CLIENTS.map(client => (
              <div key={client.name} className="px-5 py-4">
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full ${client.color} flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5`}>
                    {client.initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-semibold text-gray-80">{client.name}</p>
                      <span className="text-xs text-gray-40">Last session: {client.lastSession}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      <div><span className="text-gray-40">Theme: </span><span className="text-gray-60">{client.theme}</span></div>
                      <div><span className="text-gray-40">Breakthrough: </span><span className="text-gray-60">{client.breakthroughZone}</span></div>
                      <div><span className="text-gray-40">Next focus: </span><span className="text-gray-60">{client.nextFocus}</span></div>
                      <div><span className="text-gray-40">Progress: </span><span className="text-success-50 font-medium">{client.progressSignal}</span></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Activity feed — 1 col */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl border border-gray-20 overflow-hidden"
        >
          <div className="px-5 py-4 border-b border-gray-10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-success-50" />
              <h2 className="text-sm font-semibold text-gray-80">Activity Feed</h2>
            </div>
            <DemoLabel />
          </div>
          <div className="divide-y divide-gray-10">
            {ACTIVITY_FEED.map((item, i) => (
              <div key={i} className="px-5 py-3 flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-success-40 mt-2 flex-shrink-0" />
                <p className="text-xs text-gray-60 leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Pattern Map */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.22 }}
        className="bg-white rounded-2xl border border-gray-20 overflow-hidden"
      >
        <div className="px-5 py-4 border-b border-gray-10 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-80">Pattern Map</h2>
            <p className="text-xs text-gray-40 mt-0.5">
              Session Intelligence™ organises the language patterns that may be shaping a client's behaviour and progress
            </p>
          </div>
          <DemoLabel />
        </div>
        <div className="p-5 space-y-3">
          {PATTERN_CATEGORIES.map(cat => (
            <div key={cat.label}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-60">{cat.label}</span>
                <span className="text-xs font-bold text-gray-80">{cat.count}</span>
              </div>
              <div className="h-2 bg-gray-10 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${(cat.count / cat.max) * 100}%` }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  className="h-full bg-success-40 rounded-full"
                />
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Three cards row */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* Breakthrough Zone */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white rounded-2xl border border-gray-20 p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs text-gray-40 font-medium uppercase tracking-wide">Sample Client</p>
              <h3 className="text-sm font-bold text-gray-80">Alex</h3>
            </div>
            <DemoLabel />
          </div>
          <div className="space-y-3">
            <div>
              <p className="text-xs font-semibold text-gray-50 uppercase tracking-wide mb-1">Likely Breakthrough Zone</p>
              <p className="text-sm font-bold text-success-50">Visibility and self-expression</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-50 uppercase tracking-wide mb-1">Why this matters</p>
              <p className="text-xs text-gray-60 leading-relaxed">
                The client repeatedly expressed a desire to grow their business while avoiding posting, selling, following up, and being seen. This may suggest the issue is not only strategy, but emotional safety and identity around visibility.
              </p>
            </div>
            <div className="bg-success-5 border border-success-10 rounded-xl p-3">
              <p className="text-xs font-semibold text-success-50 mb-1">Suggested Coach Exploration</p>
              <p className="text-xs text-gray-60 italic">"What do you believe would happen if you became more visible?"</p>
            </div>
            <p className="text-xs text-gray-40 italic">This is a coaching hypothesis, not a diagnosis. Validate with the client.</p>
          </div>
        </motion.div>

        {/* Next Session Planner */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.28 }}
          className="bg-white rounded-2xl border border-gray-20 p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs text-gray-40 font-medium uppercase tracking-wide">Sample Client</p>
              <h3 className="text-sm font-bold text-gray-80">Sarah</h3>
            </div>
            <DemoLabel />
          </div>
          <div className="space-y-3">
            <div>
              <p className="text-xs font-semibold text-gray-50 uppercase tracking-wide mb-1">Suggested Focus</p>
              <p className="text-sm font-medium text-gray-80">Boundaries, guilt, and emotional responsibility</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-50 uppercase tracking-wide mb-1">Opening Question</p>
              <p className="text-xs text-gray-60 italic">"What stood out most for you from our last conversation?"</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-50 uppercase tracking-wide mb-2">Possible Coach Questions</p>
              <ul className="space-y-1.5">
                {[
                  '"What happens inside when you imagine saying no?"',
                  '"Whose disappointment are you trying to avoid?"',
                  '"What would protect your peace without disconnecting?"',
                ].map((q, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <div className="w-1 h-1 rounded-full bg-success-40 mt-2 flex-shrink-0" />
                    <p className="text-xs text-gray-60 italic">{q}</p>
                  </li>
                ))}
              </ul>
            </div>
            <Link
              to="/si/app/planner"
              className="inline-flex items-center gap-1 text-xs text-success-50 font-medium hover:underline"
            >
              View full planner <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
        </motion.div>

        {/* Progress Signals */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.31 }}
          className="bg-white rounded-2xl border border-gray-20 p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs text-gray-40 font-medium uppercase tracking-wide">Sample Client</p>
              <h3 className="text-sm font-bold text-gray-80">David</h3>
            </div>
            <DemoLabel />
          </div>
          <p className="text-xs text-gray-50 mb-3 leading-relaxed">
            Session Intelligence™ helps coaches notice shifts in client language across time — from fixed identity language toward ownership and action.
          </p>
          <div className="space-y-2">
            {PROGRESS_SIGNALS.map(s => (
              <div key={s.session} className={`border-l-4 ${s.color} rounded-r-xl pl-3 py-2`}>
                <p className="text-xs font-semibold text-gray-60">{s.session} — {s.label}</p>
                <p className="text-xs text-gray-50 italic mt-0.5">{s.quote}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* CTA bar */}
      {!isUnlocked && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="bg-gray-80 rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
        >
          <div>
            <h3 className="text-base font-bold text-white">Ready to activate your first report?</h3>
            <p className="text-sm text-gray-40 mt-1">
              Join the live walkthrough to upload your first session and generate real insights for your own clients.
            </p>
          </div>
          <a
            href={WALKTHROUGH_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-success-40 hover:bg-success-50 text-white rounded-2xl text-sm font-semibold transition-colors flex-shrink-0"
          >
            Join Live Walkthrough
            <ArrowRight className="w-4 h-4" />
          </a>
        </motion.div>
      )}
    </div>
  );
}
